import type { ChangeEvent, DragEvent, RefObject } from 'react';
import {
  lazy,
  Suspense,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { PendingPhoto } from '../../api/pending';
import { getInitials } from '../../utils/initials';
import { CropModal } from './CropModal';
import { PhotoPlate } from './PhotoPlate';
import type { NormalisedRect } from './webcam/faceFraming';
import type { Capture } from './webcam/WebcamCapture';

// The face-detection dependencies behind WebcamCapture only load once the
// camera is actually chosen — a direct import loads them for every consumer,
// which is the wall four earlier test harnesses hit (#445).
const WebcamCapture = lazy(() =>
  import('./webcam/WebcamCapture').then((m) => ({ default: m.WebcamCapture })),
);

interface PhotoBoardProps {
  currentBlob: Blob | null;
  currentFailed: boolean;
  currentLoading: boolean;
  displayName: string;
  pending: PendingPhoto | null;
  isSelf: boolean;
  /** `moderate:Photo` — arbitrate somebody else's submission. */
  canModerate: boolean;
  /** `delete:Photo`, which role `user` does not hold. */
  canDelete: boolean;
  /**
   * Sur sa propre fiche, envoyer crée une demande au lieu d'atteindre Entra —
   * vrai pour tout le monde sauf un admin. Passé en clair plutôt que déduit de
   * `canModerate` : modérer les demandes des autres ne dispense plus de la
   * sienne, et la ligne annonçait donc l'inverse de ce que le serveur fait.
   */
  selfUploadFilesRequest: boolean;
  /**
   * The reader filed this pending photo themselves. Four-eyes: the server
   * refuses their approval, so the button is disabled with the reason.
   */
  ownSubmission: boolean;
  /** Who filed the pending photo, when it was not the subject themselves. */
  submitterLabel: string | null;
  busy: boolean;
  onSubmit: (blob: Blob) => void;
  onRefresh: () => void;
  onDelete: () => void;
  onApprove: () => void;
  onReject: () => void;
  onCancel: () => void;
}

// Whether the *stream* is available, which is not the same as whether the
// reader has a camera: getUserMedia needs a secure context, so on a phone
// reaching the stack over http://<lan-ip> this is false while the device's
// own camera app is one tap away. That is what the camera fallback uses.
function isStreamSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- undefined outside a secure context (plain http), whatever the DOM typings say
    !!navigator.mediaDevices?.getUserMedia
  );
}

/**
 * The pending row as this reader gets to see it: `shownPending` is the row,
 * or null when it is not theirs to see.
 */
function pendingVisibility(
  pending: PendingPhoto | null,
  canModerate: boolean,
  isSelf: boolean,
): { isRejected: boolean; shownPending: PendingPhoto | null } {
  const isRejected = !!pending?.rejectedAt;
  // A rejected row is the subject's record: the coral tag, the reason, no
  // button. A moderator reading somebody else has nothing to do with it and
  // sees none of it — the same rule that already tells them nothing when
  // nothing is pending. On their own page they are the subject.
  const showPending =
    pending !== null && !(isRejected && canModerate && !isSelf);
  return { isRejected, shownPending: showPending ? pending : null };
}

// React Query caches the Blob; this mount owns its object URL and revokes
// it, the same lifecycle UserAvatar uses.
function useCurrentPhotoUrl(currentBlob: Blob | null): string | null {
  const currentUrl = useMemo(
    // One URL per blob, revoked by the effect below on change or unmount. A
    // useMemo is not a guarantee, so StrictMode's double render can leak one
    // URL in development; production creates exactly one.
    // react-doctor-disable-next-line react-doctor/no-create-object-url-in-render, react-doctor/no-create-object-url-without-revoke
    () => (currentBlob ? URL.createObjectURL(currentBlob) : null),
    [currentBlob],
  );
  useEffect(() => {
    if (!currentUrl) {
      return;
    }
    return () => URL.revokeObjectURL(currentUrl);
  }, [currentUrl]);
  return currentUrl;
}

/**
 * The photo being prepared in this browser: the picked or captured original,
 * the crop step over it, and the cropped result waiting to be sent — with the
 * object URLs behind both, which this hook creates and revokes.
 */
function useStagedPhoto() {
  const { t } = useTranslation();
  const [rawUrl, setRawUrl] = useState<string | null>(null);
  const [croppedUrl, setCroppedUrl] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [showCrop, setShowCrop] = useState(false);
  const [pendingCrop, setPendingCrop] = useState<NormalisedRect | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  // Revokes whatever rawUrl/croppedUrl are on unmount. An effect event reads
  // the current values, so the cleanup below can run once (empty deps)
  // instead of on every change — the replace/clear paths already revoke the
  // URLs they drop, and doing it again would double-revoke.
  const revokeCurrent = useEffectEvent(() => {
    if (rawUrl) {
      URL.revokeObjectURL(rawUrl);
    }
    if (croppedUrl) {
      URL.revokeObjectURL(croppedUrl);
    }
  });

  useEffect(() => {
    return () => {
      revokeCurrent();
    };
  }, []);

  function openFile(file: File) {
    // #496 widened the pipeline to PNG. The crop step re-encodes to JPEG
    // either way, which is why nothing downstream of it knows the difference.
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setInputError(t('photo.drop.unsupportedType'));
      return;
    }
    setInputError(null);
    if (rawUrl) {
      URL.revokeObjectURL(rawUrl);
    }
    if (croppedUrl) {
      URL.revokeObjectURL(croppedUrl);
    }
    // Revoked when replaced (just above) and on unmount (rawUrlRef /
    // croppedUrlRef cleanup).
    // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke
    setRawUrl(URL.createObjectURL(file));
    setPendingCrop(null);
    setCroppedUrl(null);
    setCroppedBlob(null);
    setShowCrop(true);
  }

  function openCapture({ blob, crop }: Capture) {
    setInputError(null);
    if (rawUrl) {
      URL.revokeObjectURL(rawUrl);
    }
    if (croppedUrl) {
      URL.revokeObjectURL(croppedUrl);
    }
    // Revoked when replaced (just above) and on unmount (rawUrlRef /
    // croppedUrlRef cleanup).
    // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke
    setRawUrl(URL.createObjectURL(blob));
    setPendingCrop(crop);
    setCroppedUrl(null);
    setCroppedBlob(null);
    setShowCrop(true);
  }

  function confirmCrop(blob: Blob) {
    if (croppedUrl) {
      URL.revokeObjectURL(croppedUrl);
    }
    setCroppedBlob(blob);
    // Revoked when replaced (just above) and on unmount (rawUrlRef /
    // croppedUrlRef cleanup).
    // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke
    setCroppedUrl(URL.createObjectURL(blob));
    setShowCrop(false);
  }

  function clear() {
    if (rawUrl) {
      URL.revokeObjectURL(rawUrl);
    }
    if (croppedUrl) {
      URL.revokeObjectURL(croppedUrl);
    }
    setRawUrl(null);
    setPendingCrop(null);
    setCroppedUrl(null);
    setCroppedBlob(null);
    setShowCrop(false);
  }

  return {
    rawUrl,
    croppedUrl,
    croppedBlob,
    showCrop,
    setShowCrop,
    pendingCrop,
    inputError,
    setInputError,
    openFile,
    openCapture,
    confirmCrop,
    clear,
  };
}

function Stamp({
  label,
  background,
  color,
}: {
  label: string;
  background: string;
  color: string;
}) {
  return (
    <span
      className="mb-1.5 inline-block px-2 py-1 text-[10px] font-bold tracking-widest uppercase"
      style={{ background, color }}
    >
      {label}
    </span>
  );
}

interface CameraPanelProps {
  streamSupported: boolean;
  busy: boolean;
  onCapture: (capture: Capture) => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
}

// Leaving the camera is a cell of the capture component's own control
// row, not a lone link under it — three stacked centred controls, two
// of them identical blue links, is what that was.
function CameraPanel({
  streamSupported,
  busy,
  onCapture,
  onFileChange,
  onClose,
}: CameraPanelProps) {
  const { t } = useTranslation();
  const captureInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex w-full flex-col items-center gap-4">
      {streamSupported ? (
        <Suspense fallback={<p className="label">{t('webcam.starting')}</p>}>
          <WebcamCapture onCapture={onCapture} onCancel={onClose} />
        </Suspense>
      ) : (
        /* No stream to draw on a canvas, so no face framing either — the
           device's own camera app returns a file, which then goes through
           the same crop step as a picked one. `capture="user"` asks for
           the front camera. */
        <>
          <p className="text-sm text-muted">
            {t('photoUpload.openCameraHint')}
          </p>
          <input
            ref={captureInputRef}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            capture="user"
            onChange={onFileChange}
            className="hidden"
          />
          <div className="plate-strip w-full">
            <button
              type="button"
              className="plate-act plate-act-go"
              disabled={busy}
              onClick={() => captureInputRef.current?.click()}
            >
              {t('photoUpload.openCamera')}
            </button>
            <button type="button" className="plate-act" onClick={onClose}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** What the current plate shows when it has no photo to show. */
function PlateFallback({
  loading,
  failed,
  displayName,
}: {
  loading: boolean;
  failed: boolean;
  displayName: string;
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <>
        <svg
          aria-hidden="true"
          className="size-8 animate-spin text-blue"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span className="label text-[10px]">{t('photo.current.loading')}</span>
      </>
    );
  }

  return (
    <>
      <span className="display text-2xl">{getInitials(displayName)}</span>
      <span className="label text-[10px] text-muted-soft">
        {failed ? t('photoViewer.loadError') : t('photoViewer.noPhotoDisplay')}
      </span>
    </>
  );
}

interface CurrentPlateProps {
  currentUrl: string | null;
  currentLoading: boolean;
  currentFailed: boolean;
  displayName: string;
  /** Something is proposed to replace this photo, so it steps back. */
  replaced: boolean;
  busy: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

function CurrentPlate({
  currentUrl,
  currentLoading,
  currentFailed,
  displayName,
  replaced,
  busy,
  fileInputRef,
  onFile,
  onFileChange,
}: CurrentPlateProps) {
  const { t } = useTranslation();
  const [isDragActive, setIsDragActive] = useState(false);

  function handleDrop(e: DragEvent) {
    // Always claimed, even when nothing will be done with it: an unclaimed
    // drop falls through to the browser's default, which is to navigate the
    // whole tab to the dropped file.
    e.preventDefault();
    setIsDragActive(false);
    if (busy) {
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) {
      onFile(file);
    }
  }

  return (
    <div className="w-[196px] max-w-full shrink-0">
      {replaced && (
        <Stamp
          label={t('photo.plate.stampNow')}
          background="var(--ink)"
          color="var(--inverse-fg)"
        />
      )}
      {/* The plate is the drop target: you drop onto the photo you are
          replacing, which is why this card has no separate dashed
          rectangle to aim at. */}
      <PhotoPlate
        src={currentUrl}
        alt={t('photo.current.alt', { name: displayName })}
        tone={isDragActive ? 'green' : 'ink'}
        dashed={isDragActive || (!currentUrl && !currentLoading)}
        ticks
        tickTone={isDragActive ? 'green' : 'blue'}
        role="button"
        tabIndex={0}
        aria-label={t('photoUpload.dropZoneLabel')}
        className={`cursor-pointer ${replaced ? 'opacity-60 grayscale' : ''}`}
        style={isDragActive ? { background: 'rgba(0,255,151,0.08)' } : {}}
        onClick={() => !busy && fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') {
            return;
          }
          e.preventDefault();
          if (!busy) {
            fileInputRef.current?.click();
          }
        }}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy && !isDragActive) {
            setIsDragActive(true);
          }
        }}
        onDragLeave={() => setIsDragActive(false)}
      >
        <PlateFallback
          loading={currentLoading}
          failed={currentFailed}
          displayName={displayName}
        />
      </PhotoPlate>
      <p className="label mt-2.5 text-center text-[9.5px] text-muted-soft">
        {t('photo.plate.dropHint')}
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
        onChange={onFileChange}
        className="hidden"
      />
    </div>
  );
}

function StagedPlate({
  croppedUrl,
  onRecrop,
}: {
  croppedUrl: string;
  onRecrop: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="w-[196px] max-w-full shrink-0">
      <Stamp
        label={t('photo.plate.stampStaged')}
        background="var(--green)"
        color="var(--ink)"
      />
      <PhotoPlate
        src={croppedUrl}
        alt={t('photoUpload.previewAlt')}
        tone="green"
        ticks
      />
      <div className="mt-2 flex justify-center">
        <button
          type="button"
          onClick={onRecrop}
          className="btn-link p-0 text-[11px]"
        >
          {t('photoUpload.recrop')}
        </button>
      </div>
    </div>
  );
}

function PendingPlate({
  pending,
  isRejected,
  displayName,
}: {
  pending: PendingPhoto;
  isRejected: boolean;
  displayName: string;
}) {
  const { t } = useTranslation();
  const tag = isRejected
    ? t('users.detail.pending.tagRejected')
    : t('users.detail.pending.tag');
  const tagBackground = isRejected ? 'var(--coral)' : 'var(--blue)';
  const tagColor = isRejected ? 'var(--inverse-fg)' : '#fff';

  return (
    <div className="w-[196px] max-w-full shrink-0">
      <Stamp label={tag} background={tagBackground} color={tagColor} />
      <PhotoPlate
        src={pending.thumbnailUrl}
        alt={t('photo.proposed.alt', { name: displayName })}
        tone={isRejected ? 'coral' : 'blue'}
        ticks
      />
      <p className="mono mt-2 text-center text-[10px] text-muted">
        {t('photo.proposed.meta', {
          kb: (pending.sizeBytes / 1024).toFixed(0),
          mime: pending.mimeType,
        })}
      </p>
    </div>
  );
}

interface ActionStripProps {
  busy: boolean;
  /** Remove is offered only with the right and a photo to remove. */
  canRemove: boolean;
  onPickFile: () => void;
  onOpenCamera: () => void;
  onRefresh: () => void;
  onDelete: () => void;
}

// One row, one voice: the four things you can do to this photo, in the
// order you would reach for them. Only sending gets a filled button, and
// only once there is something to send.
function ActionStrip({
  busy,
  canRemove,
  onPickFile,
  onOpenCamera,
  onRefresh,
  onDelete,
}: ActionStripProps) {
  const { t } = useTranslation();
  const actionButtonStyle = 'plate-act';

  return (
    <div className="plate-strip mt-5">
      <button
        type="button"
        className={actionButtonStyle}
        disabled={busy}
        onClick={onPickFile}
      >
        {t('photoUpload.sourceFile')}
      </button>
      <button
        type="button"
        className={actionButtonStyle}
        disabled={busy}
        onClick={onOpenCamera}
      >
        {t('photoUpload.sourceCamera')}
      </button>
      <button
        type="button"
        className={actionButtonStyle}
        title={t('photoViewer.refreshHint')}
        onClick={onRefresh}
      >
        {t('photoViewer.refresh')}
      </button>
      {canRemove && (
        <button
          type="button"
          className={`${actionButtonStyle} plate-act-danger`}
          disabled={busy}
          onClick={onDelete}
        >
          {t('photoViewer.remove')}
        </button>
      )}
    </div>
  );
}

function SendBar({
  busy,
  showSelfPrompt,
  onSend,
  onClear,
}: {
  busy: boolean;
  showSelfPrompt: boolean;
  onSend: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-t-rule pt-4">
      <button
        type="button"
        className="btn-primary"
        disabled={busy}
        onClick={onSend}
      >
        {busy ? t('photoUpload.sending') : t('photoUpload.sendPhoto')}
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={busy}
        className="btn-ghost disabled:opacity-50"
      >
        {t('common.cancel')}
      </button>
      {showSelfPrompt && (
        <span className="text-sm text-muted">
          {t('users.detail.update.selfPrompt')}
        </span>
      )}
    </div>
  );
}

interface PendingPanelProps {
  pending: PendingPhoto;
  isRejected: boolean;
  isSelf: boolean;
  canModerate: boolean;
  ownSubmission: boolean;
  submitterLabel: string | null;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onCancel: () => void;
}

function PendingPanel({
  pending,
  isRejected,
  isSelf,
  canModerate,
  ownSubmission,
  submitterLabel,
  busy,
  onApprove,
  onReject,
  onCancel,
}: PendingPanelProps) {
  const { t } = useTranslation();
  const canArbitrate = canModerate && !isRejected;

  return (
    <div className="mt-5 border-t border-t-rule pt-4">
      {isRejected && (
        <>
          <p className="label mb-1 text-coral">
            {t('users.detail.pending.rejectionReason')}
          </p>
          <p className="mb-4 border-l-2 border-l-coral pl-3 text-base">
            {pending.rejectionReason ?? '—'}
          </p>
        </>
      )}

      <p className="mono mb-3 text-xs text-muted">
        {t('users.detail.pending.submittedAt')}{' '}
        {new Date(pending.submittedAt).toLocaleString()}
        {submitterLabel && (
          <>
            {' · '}
            {t('admin.moderation.submittedBy')} {submitterLabel}
          </>
        )}
      </p>

      {isSelf && (
        <p className="mb-4 text-base">
          {isRejected
            ? t('users.detail.pending.selfRejected')
            : t('users.detail.pending.selfWaiting')}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        {canArbitrate && (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || ownSubmission}
              title={ownSubmission ? t('photo.pending.fourEyes') : undefined}
              onClick={onApprove}
            >
              {t('users.detail.pending.approve')}
            </button>
            <button
              type="button"
              className="btn-ghost border-coral text-coral"
              disabled={busy}
              onClick={onReject}
            >
              {t('users.detail.pending.reject')}
            </button>
          </>
        )}
        {/* Rejected rows included. `DELETE /users/me/pending` deletes
            whatever row the subject has, refused or not, so hiding this
            on a refusal left them looking at a verdict they could not
            clear: the row expires only with the TTL sweep, and until it
            did the card carried "moche" under their photo. Withdrawing a
            refusal is not undoing it — the reject is in the audit log. */}
        {isSelf && (
          <button
            type="button"
            className="btn-link"
            disabled={busy}
            onClick={onCancel}
          >
            {isRejected
              ? t('photo.pending.dismiss')
              : t('photo.pending.cancel')}
          </button>
        )}
      </div>

      {/* The disabled Approve explains itself for pointer users via its
          title; this line says it for everyone else. */}
      {canArbitrate && ownSubmission && (
        <p className="mt-3 text-sm text-muted">{t('photo.pending.fourEyes')}</p>
      )}
    </div>
  );
}

/**
 * The body of the photo card: the photo Entra holds, whatever is proposed to
 * replace it, and every way to act on either.
 *
 * One component rather than the viewer/pair/dropzone split it replaces,
 * because this design has one row of plates fed from two places — a photo
 * filed for moderation on the server, and a crop staged in this browser — and
 * splitting them across components meant either two components rendering the
 * same row conditionally, or lifting the staged blob out of the component that
 * owns its object URLs. The plate is also the drop target, so the file
 * handling and the photo it replaces have to live together.
 *
 * Pure by construction: props in, callbacks out, no api module at runtime,
 * which is what lets the visibility matrix be tested as a table of props.
 */
export function PhotoBoard({
  currentBlob,
  currentFailed,
  currentLoading,
  displayName,
  pending,
  isSelf,
  canModerate,
  canDelete,
  selfUploadFilesRequest,
  ownSubmission,
  submitterLabel,
  busy,
  onSubmit,
  onRefresh,
  onDelete,
  onApprove,
  onReject,
  onCancel,
}: PhotoBoardProps) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUrl = useCurrentPhotoUrl(currentBlob);
  const staged = useStagedPhoto();
  const { croppedUrl, croppedBlob, inputError } = staged;

  const streamSupported = isStreamSupported();

  function handleCapture(capture: Capture) {
    staged.openCapture(capture);
    setCameraOpen(false);
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      staged.openFile(file);
    }
    e.target.value = '';
  }

  const hasPhoto = !!currentUrl;
  const { isRejected, shownPending } = pendingVisibility(
    pending,
    canModerate,
    isSelf,
  );

  // One second plate, and a crop staged in this browser takes it: it is the
  // thing the reader is working on. A photo already filed for moderation
  // keeps its tag and its buttons below either way.
  const hasSecond = !!croppedUrl || shownPending !== null;

  return (
    // A container, so the action strip can count its columns from the card's
    // own width — the page puts this card in a grid cell half the window wide.
    <div className="@container">
      {cameraOpen ? (
        <CameraPanel
          streamSupported={streamSupported}
          busy={busy}
          onCapture={handleCapture}
          onFileChange={handleFileChange}
          onClose={() => setCameraOpen(false)}
        />
      ) : (
        <>
          {/* Centred: with the specification list gone the plate was alone
              against the left edge of a card three times its width. The
              diptych centres as a group, so the arrow stays in the middle of
              the card and the pair reads as one row. */}
          <div className="flex flex-wrap items-start justify-center gap-6">
            <CurrentPlate
              currentUrl={currentUrl}
              currentLoading={currentLoading}
              currentFailed={currentFailed}
              displayName={displayName}
              replaced={hasSecond}
              busy={busy}
              fileInputRef={fileInputRef}
              onFile={staged.openFile}
              onFileChange={handleFileChange}
            />

            {hasSecond && (
              <span
                aria-hidden="true"
                className="self-center text-2xl text-muted-soft"
              >
                →
              </span>
            )}
            {croppedUrl ? (
              <StagedPlate
                croppedUrl={croppedUrl}
                onRecrop={() => staged.setShowCrop(true)}
              />
            ) : (
              shownPending && (
                <PendingPlate
                  pending={shownPending}
                  isRejected={isRejected}
                  displayName={displayName}
                />
              )
            )}
          </div>

          <ActionStrip
            busy={busy}
            canRemove={hasPhoto && canDelete}
            onPickFile={() => fileInputRef.current?.click()}
            onOpenCamera={() => {
              staged.setInputError(null);
              setCameraOpen(true);
            }}
            onRefresh={onRefresh}
            onDelete={onDelete}
          />
        </>
      )}

      {inputError && (
        <p role="alert" className="mt-4 text-sm text-coral">
          {inputError}
        </p>
      )}

      {croppedBlob && (
        <SendBar
          busy={busy}
          showSelfPrompt={isSelf && selfUploadFilesRequest}
          onSend={() => onSubmit(croppedBlob)}
          onClear={staged.clear}
        />
      )}

      {shownPending && (
        <PendingPanel
          pending={shownPending}
          isRejected={isRejected}
          isSelf={isSelf}
          canModerate={canModerate}
          ownSubmission={ownSubmission}
          submitterLabel={submitterLabel}
          busy={busy}
          onApprove={onApprove}
          onReject={onReject}
          onCancel={onCancel}
        />
      )}

      {staged.showCrop && staged.rawUrl && (
        <CropModal
          src={staged.rawUrl}
          initialCrop={staged.pendingCrop}
          onCancel={() => staged.setShowCrop(false)}
          onConfirm={staged.confirmCrop}
        />
      )}
    </div>
  );
}
